import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import { ExpandOutlined, BorderOuterOutlined } from '@ant-design/icons';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  ControlButton,
  useNodesState,
  useEdgesState,
  MarkerType,
  useReactFlow,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useThemeStyles } from '../context/ThemeContext';
import {
  shouldFitOnExternalTrigger,
  shouldFitOnVisibilityChange,
  scheduleMeasuredFitView,
  CHAT_GRAPH_FIT_OPTIONS,
} from './chatGraphFitPolicy';
import {
  layoutRelationshipGraph,
  genericNodeContextLabel,
  RELATIONSHIP_NODE_MIN_WIDTH,
  RELATIONSHIP_NODE_MIN_HEIGHT,
  RELATIONSHIP_NODE_MAX_WIDTH,
} from './relationshipGraphLayout';
import './ChatGraphPanel.css';

const PORT_POSITION = {
  top: Position.Top,
  bottom: Position.Bottom,
  left: Position.Left,
  right: Position.Right,
};

/** Node/edge chrome for chat relationship maps */
const GRAPH_VISUAL = {
  dark: {
    canvasBg: 'rgba(0, 0, 0, 0.25)',
    modalBg: 'rgba(0, 0, 0, 0.35)',
    bgDots: 'rgba(255, 255, 255, 0.04)',
    nodeMain: {
      background: 'rgba(35, 76, 173, 0.95)',
      color: '#e8e4ff',
      border: '1px solid rgba(153, 198, 255, 0.95)',
      boxShadow: '0 0 0 1px rgba(58, 124, 255, 0.45), 0 6px 16px rgba(27, 64, 145, 0.48)',
    },
    nodeDefault: {
      background: 'rgba(31, 31, 42, 0.92)',
      color: '#e8e4ff',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      boxShadow: 'none',
    },
    edgeDirect: { stroke: 'rgba(242, 46, 151, 0.85)', strokeWidth: 1.8 },
    edgeIndirect: { stroke: 'rgba(86, 180, 255, 0.75)', strokeWidth: 1.4, strokeDasharray: '6 4' },
    markerDirect: 'rgba(242, 46, 151, 0.9)',
    markerIndirect: 'rgba(86, 180, 255, 0.85)',
    labelShowBg: true,
    labelStyle: { fill: '#f8fafc', fontSize: 10, fontWeight: 600 },
    labelBgStyle: { fill: 'rgba(15, 23, 42, 0.92)', stroke: 'rgba(255, 255, 255, 0.2)', strokeWidth: 1 },
  },
  light: {
    canvasBg: '#f8fafc',
    modalBg: '#f1f5f9',
    bgDots: '#cbd5e1',
    nodeMain: {
      background: '#eff6ff',
      color: '#1e3a8a',
      border: '1.5px solid #2563eb',
      boxShadow: '0 1px 4px rgba(37, 99, 235, 0.2)',
    },
    nodeDefault: {
      background: '#ffffff',
      color: '#0f172a',
      border: '1.5px solid #64748b',
      boxShadow: '0 1px 4px rgba(15, 23, 42, 0.12)',
    },
    /* Match dark theme: neon pink direct edges, cyan indirect */
    edgeDirect: { stroke: 'rgba(246, 46, 151, 0.88)', strokeWidth: 1.8 },
    edgeIndirect: { stroke: 'rgba(86, 180, 255, 0.75)', strokeWidth: 1.4, strokeDasharray: '6 4' },
    markerDirect: 'rgba(246, 46, 151, 0.95)',
    markerIndirect: 'rgba(86, 180, 255, 0.85)',
    labelShowBg: true,
    labelStyle: { fill: '#1e293b', fontSize: 10, fontWeight: 600 },
    labelBgStyle: { fill: '#ffffff', stroke: '#cbd5e1', strokeWidth: 1 },
  },
};

function pickVisualGraph(entry) {
  if (!entry) return null;
  if (entry.graph_schema && entry.graph_schema.nodes?.length) {
    return entry.graph_schema;
  }
  const inner = entry.graph != null ? entry.graph : entry;
  if (!inner) return null;
  if (inner.graph_schema && inner.graph_schema.nodes?.length) {
    return inner.graph_schema;
  }
  const raw = inner.raw || {};
  return (
    raw.graph ||
    raw.graph_payload ||
    raw.technical_payload?.graph_payload ||
    (raw.technical_payload?.graph?.nodes ? raw.technical_payload.graph : null) ||
    null
  );
}

/** Resolve edge endpoints by node id only — never by label or display name. */
function normalizeGraphSchemaForUi(schema) {
  if (!schema || !Array.isArray(schema.nodes)) {
    return { nodes: [], edges: [] };
  }

  const seenIds = new Set();
  const nodes = [];
  for (const n of schema.nodes) {
    if (!n || n.id == null) continue;
    const id = String(n.id).trim();
    if (!id || seenIds.has(id)) continue;
    seenIds.add(id);
    nodes.push({ ...n, id });
  }

  const nodeIds = seenIds;

  const rawEdges = Array.isArray(schema.edges)
    ? schema.edges
    : Array.isArray(schema.links)
      ? schema.links
      : [];

  const resolveEndpointId = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') {
      const nested =
        value.id
        ?? value.node_id
        ?? value.nodeId;
      return resolveEndpointId(nested);
    }
    const key = String(value).trim();
    if (!key) return '';
    return nodeIds.has(key) ? key : '';
  };

  const edges = rawEdges
    .map((e) => {
      if (!e || typeof e !== 'object') return null;
      const srcRaw = e.source ?? e.from ?? e.start ?? e.source_id ?? e.sourceId ?? e.src;
      const tgtRaw = e.target ?? e.to ?? e.end ?? e.target_id ?? e.targetId ?? e.dst;
      const source = resolveEndpointId(srcRaw);
      const target = resolveEndpointId(tgtRaw);
      if (!source || !target) return null;
      return { ...e, source, target };
    })
    .filter(Boolean);

  return { ...schema, nodes, edges };
}

function layoutFlowElements(schema, isLight = false, containerSize = null) {
  const visual = isLight ? GRAPH_VISUAL.light : GRAPH_VISUAL.dark;
  const normalized = normalizeGraphSchemaForUi(schema);
  if (!normalized?.nodes?.length) {
    return { nodes: [], edges: [] };
  }
  const items = normalized.nodes;
  const links = normalized.edges || [];

  const layout = layoutRelationshipGraph(items, links, {
    containerWidth: containerSize?.width || 920,
    containerHeight: containerSize?.height || 360,
    rankdir: 'LR',
    nodesep: 56,
    ranksep: 96,
  });
  const { positions, sizedNodes, portHints = {} } = layout;
  const sizeById = Object.fromEntries(sizedNodes.map((n) => [n.id, n]));

  // Same display label may appear on distinct IDs — never merge; expose generic context instead.
  const labelCounts = {};
  items.forEach((n) => {
    const key = String(n.label || n.id || '').trim().toLowerCase();
    labelCounts[key] = (labelCounts[key] || 0) + 1;
  });

  const outDegree = {};
  items.forEach((n) => {
    outDegree[n.id] = 0;
  });
  links.forEach((e) => {
    if (e.source) outDegree[e.source] = (outDegree[e.source] || 0) + 1;
  });
  let hub = items[0]?.id || '';
  items.forEach((n) => {
    if ((outDegree[n.id] || 0) > (outDegree[hub] || 0)) hub = n.id;
  });
  const mainNodeIds = new Set(
    items.filter((n) => (outDegree[n.id] || 0) >= 2).map((n) => n.id),
  );
  if (hub) mainNodeIds.add(hub);

  const classifyDependency = (edge) => {
    const depType = String(
      edge?.properties?.dependency_type
      || edge?.dependency_type
      || '',
    ).toLowerCase();
    const relation = String(edge?.type || edge?.label || '').toLowerCase();
    const hop = Number(edge?.properties?.hop_count ?? edge?.hop_count);
    if (depType.includes('indirect') || relation.includes('indirect')) return 'indirect';
    if (Number.isFinite(hop) && hop > 1) return 'indirect';
    return 'direct';
  };

  const toTitleCaseLabel = (value) =>
    String(value || '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const truncateEdgeLabel = (value, max = 22) => {
    const text = String(value || '').trim();
    if (!text) return '';
    return text.length > max ? `${text.slice(0, max - 1)}…` : text;
  };

  const getEdgeRelationshipLabel = (edge) => {
    const raw =
      edge?.properties?.relationship_type
      || edge?.relationship_type
      || edge?.properties?.relation_type
      || edge?.relation_type
      || edge?.properties?.relationship
      || edge?.relationship
      || edge?.type
      || edge?.label
      || '';
    const normalizedLabel = String(raw || '').trim().toLowerCase();
    if (!normalizedLabel || normalizedLabel === 'edge') return '';
    return truncateEdgeLabel(toTitleCaseLabel(raw));
  };

  const flowNodes = items.map((n) => {
    const roleHint = String(n?.type || n?.role || n?.properties?.role || '').toLowerCase();
    const isPathNode = roleHint === 'path' || roleHint === 'answer_path' || roleHint === 'sequence';
    const isMainNode =
      isPathNode
      || mainNodeIds.has(n.id)
      || roleHint.includes('main')
      || roleHint.includes('primary')
      || roleHint.includes('orchestrator')
      || roleHint.includes('api');
    const nodeChrome = isMainNode ? visual.nodeMain : visual.nodeDefault;
    const sized = sizeById[n.id] || {
      width: RELATIONSHIP_NODE_MIN_WIDTH,
      height: RELATIONSHIP_NODE_MIN_HEIGHT,
    };
    const fullLabel = String(n.label || n.id || '');
    const context = genericNodeContextLabel(n);
    const labelKey = fullLabel.trim().toLowerCase();
    // Only surface context when the same display label appears on distinct IDs.
    const showContext = Boolean(context) && labelCounts[labelKey] > 1;
    const ports = portHints[n.id] || { sourcePosition: 'right', targetPosition: 'left' };
    const displayHeight = showContext
      ? Math.max(sized.height, RELATIONSHIP_NODE_MIN_HEIGHT + 16)
      : sized.height;
    return {
      id: n.id,
      position: positions[n.id] || { x: 0, y: 0 },
      sourcePosition: PORT_POSITION[ports.sourcePosition] || Position.Right,
      targetPosition: PORT_POSITION[ports.targetPosition] || Position.Left,
      data: {
        label: showContext ? `${fullLabel}\n${context}` : fullLabel,
        primaryLabel: fullLabel,
        contextLabel: showContext ? context : '',
      },
      style: {
        ...nodeChrome,
        fontSize: 12,
        lineHeight: 1.25,
        fontWeight: 600,
        padding: '10px 12px',
        borderRadius: 8,
        minWidth: RELATIONSHIP_NODE_MIN_WIDTH,
        minHeight: RELATIONSHIP_NODE_MIN_HEIGHT,
        width: sized.width,
        height: displayHeight,
        maxWidth: RELATIONSHIP_NODE_MAX_WIDTH,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      title: showContext ? `${fullLabel} (${context})` : fullLabel,
    };
  });

  const flowEdges = links.map((e, i) => {
    const depKind = classifyDependency(e);
    const relationshipLabel = getEdgeRelationshipLabel(e);
    const isIndirect = depKind === 'indirect';
    return {
      id: String(e.id || `e-${i}-${e.source}-${e.target}`),
      source: e.source,
      target: e.target,
      type: 'smoothstep',
      pathOptions: { borderRadius: 10, offset: 12 },
      label: relationshipLabel || undefined,
      labelShowBg: visual.labelShowBg,
      labelBgPadding: [5, 3],
      labelBgBorderRadius: 4,
      labelStyle: {
        ...visual.labelStyle,
        fontSize: 9,
        fontWeight: 600,
      },
      labelBgStyle: visual.labelBgStyle,
      style: {
        ...(isIndirect ? visual.edgeIndirect : visual.edgeDirect),
        strokeWidth: isIndirect ? 1.5 : 2,
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: isIndirect ? visual.markerIndirect : visual.markerDirect,
        width: 18,
        height: 18,
      },
    };
  });

  return { nodes: flowNodes, edges: flowEdges, layoutMeta: layout.meta };
}

const AGENT_LABEL_BY_ROUTE = {
  graphrag: 'Agent : Graph Agent',
  graph: 'Agent : Graph Agent',
  hybrid: 'Agent : Graph Agent',
  rag: 'Agent : RAG Agent',
  deep: 'Agent : Deep',
};

function formatRouteSubtitle(route) {
  if (!route) return '';
  const key = String(route).trim().toLowerCase();
  if (AGENT_LABEL_BY_ROUTE[key]) return AGENT_LABEL_BY_ROUTE[key];
  return `Agent : ${String(route)}`;
}

function GraphFlowInner({ schema, height, onRequestExpand, fitTrigger, isLight }) {
  const { fitView } = useReactFlow();
  const visual = isLight ? GRAPH_VISUAL.light : GRAPH_VISUAL.dark;
  const wrapRef = useRef(null);
  const wasIntersectingRef = useRef(false);
  const userAdjustedViewportRef = useRef(false);
  // After content/visibility changes, allow one more fit when React Flow publishes node dimensions.
  const needsMeasuredFitRef = useRef(true);
  const [containerSize, setContainerSize] = useState({
    width: 920,
    height: typeof height === 'number' ? height : 320,
  });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      setContainerSize((prev) => {
        if (Math.abs(prev.width - rect.width) < 24 && Math.abs(prev.height - rect.height) < 24) {
          return prev;
        }
        return {
          width: Math.max(280, rect.width || prev.width),
          height: Math.max(180, rect.height || prev.height),
        };
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const laidOut = useMemo(
    () => layoutFlowElements(schema, isLight, containerSize),
    [schema, isLight, containerSize],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(laidOut.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(laidOut.edges);
  const [hoveredNodeId, setHoveredNodeId] = useState(null);

  const neighborIds = useMemo(() => {
    if (!hoveredNodeId) return null;
    const set = new Set([hoveredNodeId]);
    edges.forEach((e) => {
      if (e.source === hoveredNodeId) set.add(e.target);
      if (e.target === hoveredNodeId) set.add(e.source);
    });
    return set;
  }, [hoveredNodeId, edges]);

  const displayNodes = useMemo(() => {
    if (!neighborIds) return nodes;
    return nodes.map((n) => {
      const active = neighborIds.has(n.id);
      return {
        ...n,
        style: {
          ...n.style,
          opacity: active ? 1 : 0.28,
          filter: active ? 'none' : 'grayscale(0.35)',
          transition: 'opacity 120ms ease',
        },
      };
    });
  }, [nodes, neighborIds]);

  const displayEdges = useMemo(() => {
    if (!hoveredNodeId) return edges;
    return edges.map((e) => {
      const active = e.source === hoveredNodeId || e.target === hoveredNodeId;
      return {
        ...e,
        animated: active,
        style: {
          ...e.style,
          opacity: active ? 1 : 0.12,
          strokeWidth: active ? Math.max(2.4, Number(e.style?.strokeWidth) || 2) : 1,
        },
        labelStyle: {
          ...e.labelStyle,
          opacity: active ? 1 : 0.15,
        },
      };
    });
  }, [edges, hoveredNodeId]);


  // Stable content fingerprint — fit on graph/theme change, not on every React Flow node mutation.
  const contentKey = useMemo(() => {
    const ns = Array.isArray(schema?.nodes) ? schema.nodes : [];
    const es = Array.isArray(schema?.edges) ? schema.edges : [];
    return [
      isLight ? 'l' : 'd',
      ns.length,
      es.length,
      ns.map((n) => String(n.id || '')).join('|'),
      es.map((e) => `${e.source || ''}->${e.target || ''}:${e.id || ''}`).join('|'),
      Math.round(containerSize.width),
      Math.round(containerSize.height),
    ].join('::');
  }, [schema, isLight, containerSize]);

  const scheduleFit = useCallback(() => scheduleMeasuredFitView(fitView), [fitView]);

  const requestFit = useCallback(() => {
    if (userAdjustedViewportRef.current) return undefined;
    needsMeasuredFitRef.current = true;
    return scheduleFit();
  }, [scheduleFit]);

  const handleExplicitFit = useCallback(() => {
    userAdjustedViewportRef.current = false;
    needsMeasuredFitRef.current = false;
    scheduleFit();
  }, [scheduleFit]);

  useEffect(() => {
    const { nodes: nn, edges: ee } = layoutFlowElements(schema, isLight, containerSize);
    setNodes(nn);
    setEdges(ee);
    // New graph content / container geometry: allow an initial fit.
    userAdjustedViewportRef.current = false;
    needsMeasuredFitRef.current = true;
  }, [contentKey, schema, isLight, containerSize, setNodes, setEdges]);

  // Fit after layout commit when graph content changes (embedded + modal mount).
  useEffect(() => {
    if (!Array.isArray(schema?.nodes) || schema.nodes.length === 0) return undefined;
    return requestFit();
  }, [contentKey, schema, requestFit]);

  // Modal (and other hosts) can bump an external trigger after the container is sized.
  useEffect(() => {
    if (!shouldFitOnExternalTrigger(fitTrigger)) return undefined;
    userAdjustedViewportRef.current = false;
    return requestFit();
  }, [fitTrigger, requestFit]);

  // Collapse/reopen (or any hide->show): refit once when the canvas becomes visible again.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const visible = entry.isIntersecting && entry.intersectionRatio > 0;
          if (shouldFitOnVisibilityChange(wasIntersectingRef.current, visible)) {
            userAdjustedViewportRef.current = false;
            requestFit();
          }
          wasIntersectingRef.current = visible;
        }
      },
      { threshold: [0, 0.01, 0.1] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [requestFit]);

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);
      if (
        needsMeasuredFitRef.current &&
        !userAdjustedViewportRef.current &&
        Array.isArray(changes) &&
        changes.some((c) => c && c.type === 'dimensions')
      ) {
        needsMeasuredFitRef.current = false;
        scheduleFit();
      }
    },
    [onNodesChange, scheduleFit],
  );

  const h = typeof height === 'number' ? `${height}px` : height;

  return (
    <div ref={wrapRef} style={{ width: '100%', height: h, position: 'relative' }}>
      <ReactFlow
        nodes={displayNodes}
        edges={displayEdges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeMouseEnter={(_evt, node) => setHoveredNodeId(node.id)}
        onNodeMouseLeave={() => setHoveredNodeId(null)}
        onPaneClick={() => setHoveredNodeId(null)}
        onMoveStart={() => {
          userAdjustedViewportRef.current = true;
          needsMeasuredFitRef.current = false;
        }}
        minZoom={0.2}
        maxZoom={1.4}
        fitViewOptions={CHAT_GRAPH_FIT_OPTIONS}
        proOptions={{ hideAttribution: true }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.75 }}
        style={{ width: '100%', height: '100%' }}
      >
        <Background color={visual.bgDots} gap={16} />
        <Controls
          showInteractive={false}
          showZoom
          showFitView={false}
          position="bottom-left"
          className="chat-graph-controls"
        >
          <ControlButton onClick={handleExplicitFit} title="Fit view" aria-label="Fit view">
            <BorderOuterOutlined style={{ fontSize: 15 }} />
          </ControlButton>
          {typeof onRequestExpand === 'function' ? (
            <ControlButton onClick={onRequestExpand} title="Expand graph" aria-label="Expand graph">
              <ExpandOutlined style={{ fontSize: 16 }} />
            </ControlButton>
          ) : null}
        </Controls>
      </ReactFlow>
    </div>
  );
}

function GraphCanvas({ schema, isLight }) {
  const [expanded, setExpanded] = useState(false);
  const [modalFitTick, setModalFitTick] = useState(0);
  // Taller canvas for TB sequential flows so short edges stay readable after fitView.
  const nodeCount = Array.isArray(schema?.nodes) ? schema.nodes.length : 0;
  const embeddedHeight = Math.min(620, Math.max(380, 320 + Math.min(nodeCount, 16) * 36));
  const visual = isLight ? GRAPH_VISUAL.light : GRAPH_VISUAL.dark;
  const flowClass = ['chat-graph-panel__flow', isLight ? 'chat-graph-panel__flow--light' : 'chat-graph-panel__flow--dark'].join(
    ' ',
  );

  return (
    <>
      <div
        className={flowClass}
        style={{
          width: '100%',
          height: embeddedHeight,
          borderRadius: 8,
          overflow: 'hidden',
          background: visual.canvasBg,
          border: isLight ? '1px solid #e2e8f0' : 'none',
          position: 'relative',
        }}
      >
        <ReactFlowProvider>
          <GraphFlowInner
            schema={schema}
            height={embeddedHeight}
            onRequestExpand={() => setExpanded(true)}
            isLight={isLight}
          />
        </ReactFlowProvider>
      </div>
      <Modal
        open={expanded}
        onCancel={() => setExpanded(false)}
        footer={null}
        width="96vw"
        rootClassName="chat-graph-modal"
        style={{ top: 20, paddingBottom: 0 }}
        styles={{
          body: {
            padding: 0,
            height: '88vh',
            maxHeight: '88vh',
            overflow: 'hidden',
            position: 'relative',
          },
        }}
        destroyOnClose
        afterOpenChange={(open) => {
          if (open) setModalFitTick((t) => t + 1);
        }}
      >
        <div
          className={`${flowClass} chat-graph-modal__pane`}
          style={{
            width: '100%',
            height: '88vh',
            minHeight: 0,
            background: visual.modalBg,
            border: isLight ? '1px solid #e2e8f0' : 'none',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <ReactFlowProvider>
            <GraphFlowInner schema={schema} height="88vh" fitTrigger={modalFitTick} isLight={isLight} />
          </ReactFlowProvider>
        </div>
      </Modal>
    </>
  );
}

/**
 * Renders one or more graph visualizations from chat API graph / graphs fields.
 * `graphs` items: { sub_query?, route?, label?, graph?, graph_schema? }
 */
export default function ChatGraphPanel({ graph, graphs, graphSchema, graphMergeMeta }) {
  const themeStyles = useThemeStyles();
  const isLight = themeStyles.isLight;
  const mutedText = isLight ? '#64748b' : 'rgba(255,255,255,0.45)';
  const faintText = isLight ? '#94a3b8' : 'rgba(255,255,255,0.28)';
  const codeColor = isLight ? '#475569' : 'rgba(255,255,255,0.6)';

  const entries = useMemo(() => {
    // Prefer the persisted top-level snapshot so history reopen matches the live turn.
    if (graphSchema && graphSchema.nodes?.length) {
      return [
        {
          key: 'g-schema-snapshot',
          title: 'Relationship Map',
          subtitle: '',
          schema: graphSchema,
        },
      ];
    }
    if (Array.isArray(graphs) && graphs.length > 0) {
      return graphs.map((g, i) => ({
        key: `g-${i}-${(g.sub_query || '').slice(0, 24)}`,
        title: g.label || g.sub_query || `Graph ${i + 1}`,
        subtitle: formatRouteSubtitle(g.route),
        schema: g.graph_schema || pickVisualGraph(g),
      }));
    }
    const single = pickVisualGraph({ graph });
    if (single && single.nodes?.length) {
      return [{ key: 'g-single', title: 'Relationship Map', subtitle: '', schema: single }];
    }
    return [];
  }, [graph, graphs, graphSchema]);

  if (!entries.length) {
    return null;
  }

  const hasRenderable = entries.some((e) => e.schema && e.schema.nodes?.length);
  if (!hasRenderable) {
    return (
      <div style={{ fontSize: 12, color: mutedText, padding: '4px 0' }}>
        The graph agent returned data, but no node/edge structure could be read. If this persists, verify the
        GraphRAG response includes <code style={{ color: codeColor }}>graph.nodes</code> and{' '}
        <code style={{ color: codeColor }}>graph.edges</code>.
      </div>
    );
  }

  const mergeHint =
    graphMergeMeta &&
    graphMergeMeta.part_count > 1 &&
    Array.isArray(graphMergeMeta.source_sub_queries) &&
    graphMergeMeta.source_sub_queries.length > 0
      ? graphMergeMeta.source_sub_queries
          .filter(Boolean)
          .slice(0, 6)
          .join(' · ')
      : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {mergeHint ? (
        <div
          style={{
            fontSize: 11,
            color: mutedText,
            lineHeight: 1.45,
            marginBottom: -4,
          }}
          title={graphMergeMeta.source_sub_queries.filter(Boolean).join('\n')}
        >
          Combined subgraphs: {mergeHint}
          {graphMergeMeta.source_sub_queries.filter(Boolean).length > 6 ? ' …' : ''}
        </div>
      ) : null}
      {entries.map((entry) =>
        entry.schema ? (
          <div key={entry.key}>
            <div style={{ marginBottom: 8, fontSize: 11, color: mutedText }}>
              {entry.title}
              {entry.subtitle ? (
                <span style={{ marginLeft: 8, color: faintText }}>{entry.subtitle}</span>
              ) : null}
            </div>
            <GraphCanvas schema={entry.schema} isLight={isLight} />
          </div>
        ) : null
      )}
    </div>
  );
}
